import {action, computed, observable} from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    inject,
    Modal,
    ModalHandle,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';

import {
    ButtonDropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
} from 'reactstrap';
import { AccountStore, IUserGridColumnPreset } from '../../Account/AccountStore';
import { AppStore } from '../../Core/AppStore';
import { DisplayListViewModel } from '../../DisplayFilters/DisplayListViewModel';
import { CreateGridColumnPresetModal } from './CreateGridColumnPresetModal';
import {BsLayoutTextSidebarReverse, BsRepeat} from "react-icons/bs";

export interface IDisplayGridColumnPresetsProps {
    readonly vm: DisplayListViewModel;
    readonly className?: string;
    readonly link?: boolean;
}

@autoinject
@observer
export class DisplayGridColumnPresets extends React.Component<IDisplayGridColumnPresetsProps> {
    public render() {
        return (
            <>
                <ButtonDropdown
                    className={this.props.className}
                    direction="down"
                    isOpen={this.dropdownOpen}
                    toggle={this.onDropdownToggle}
                >
                    <DropdownToggle caret color={this.props.link ? 'link' : undefined} size="sm" className="f-18">
                            <BsLayoutTextSidebarReverse />
                    </DropdownToggle>                    

                    <DropdownMenu end className="py-0">
                        <DropdownItem header className="fw-bold">
                            Column Presets
                        </DropdownItem>

                        <DropdownItem divider className="m-0" />
                        <Input
                            type="text"
                            placeholder="Find Preset..."
                            value={this.findString}
                            onChange={this.onFindChange}
                        />
                        <DropdownItem
                            className="text-center dropdown-header"
                            onClick={this.openCreatePresetModal}
                        >
                            Create New Preset
                        </DropdownItem>

                        <DropdownItem>
                            <div className="mb-2">
                                <label>Auto Update Presets</label>
                                <Input type="switch" role="switch" className="ms-2"
                                       defaultChecked={this.accountStore.storage.gridAutoUpdatePresets}
                                       onClick={this.onToggleAutoUpdatePresets}/>
                            </div>
                        </DropdownItem>

                        {this.gridColumnPresets?.length > 0 && this.gridColumnPresets.map((p, k) =>
                            <DropdownItem
                                key={k}
                                className={this.getIsGridPresetSelected(p)
                                    ? 'd-flex align-items-center pe-1 bg-gray-light'
                                    : 'd-flex align-items-center pe-1'
                                }
                                onClick={() => this.onPresetChange(p)}
                            >
                                {p.name}

                                <div className="d-inline-block ms-auto">
                                    <Tooltip message="Overwrite" className="p-0">
                                        <a className="btn btn-link px-1"
                                           onClick={(e) => {
                                               e.stopPropagation();
                                               this.overwritePreset(p);
                                           }}
                                        >
                                            <BsRepeat className="text-orange f-14"/>
                                        </a>
                                    </Tooltip>

                                    <a className="btn btn-link px-1" onClick={() => this.deletePresetAsync(p)}>
                                    <BsTrash className="text-danger"/>
                                    </a>
                                </div>
                            </DropdownItem>
                        )}
                    </DropdownMenu>
                </ButtonDropdown>

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.confirmModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">Delete Preset?</div>
                </Modal>

                <Modal
                    className="modal-sm"
                    title="Confirm Overwrite"
                    modalHandle={this.confirmOverwriteModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">Overwrite Preset?</div>
                </Modal>

                <CreateGridColumnPresetModal modalHandle={this.createPresetModalHandle} />
            </>
        );
    }

    @bind
    @action
    private onToggleAutoUpdatePresets(event: React.MouseEvent<HTMLInputElement>) {
        event.stopPropagation();
        this.accountStore.storage.gridAutoUpdatePresets = !this.accountStore.storage.gridAutoUpdatePresets;
    }
    
    @bind
    @action
    private async overwritePreset(preset: IUserGridColumnPreset) {
        if (await this.confirmOverwriteModalHandle.confirm()) {
            // Remove the existing preset with the same name
            this.accountStore.storage.gridColumnPresets = this.accountStore.storage.gridColumnPresets.filter(
                (p: IUserGridColumnPreset) => p.name !== preset.name
            );

            // Update the columns
            preset.columns = this.accountStore.tabStorage.activeGridColumns;

            // Add the updated preset to the beginning of the array
            this.accountStore.storage.gridColumnPresets.unshift(preset);

            // Set the active preset name
            this.accountStore.tabStorage.activeGridColumnPreset = preset.name;
        }
    }

    @bind
    @action
    private getIsGridPresetSelected(preset: IUserGridColumnPreset) {
        if (this.activePreset == preset.name) return true;

        return false;
    }

    @bind
    @action
    private onDropdownToggle() {
        this.dropdownOpen = !this.dropdownOpen;
    }

    @bind
    @action
    private async deletePresetAsync(preset: IUserGridColumnPreset) {
        if (await this.confirmModalHandle.confirm()) {
            if (this.activePreset == preset.name) {
                this.accountStore.storage.activeGridColumnPreset = '';
                this.accountStore.tabStorage.activeGridColumnPreset = '';
            }
            const index = this.accountStore.storage.gridColumnPresets.findIndex(c => c.name == preset.name);
            this.accountStore.storage.gridColumnPresets.splice(index, 1);
        }
    }

    @bind
    @action
    private onPresetChange(preset: IUserGridColumnPreset) {
        if (!this.confirmModalHandle.isShowing) {
            this.accountStore.storage.activeGridColumnPreset = preset.name;
            this.accountStore.tabStorage.activeGridColumnPreset = preset.name;
            
            this.appStore.gridRef?.api.applyColumnState({ state: preset.columns, applyOrder: true });
        }
    }

    @bind
    @action
    private openCreatePresetModal(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.createPresetModalHandle.open();
    }

    @bind
    @action
    private onFindChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.findString = target.value;
    }
    
    @computed
    private get gridColumnPresets() {
        return this.accountStore.storage.gridColumnPresets;
    }
    
    @computed
    private get activePreset() {
        return this.accountStore.tabStorage.activeGridColumnPreset ?? this.accountStore.storage.activeGridColumnPreset;
    }

    @observable
    private findString: string = '';
    @observable
    private dropdownOpen: boolean = false;
    @inject
    private accountStore!: AccountStore;
    @inject
    private appStore!: AppStore;
    private createPresetModalHandle = new ModalHandle();
    private createPresetStickyModalHandle = new ModalHandle();
    private confirmModalHandle = new ModalHandle();
    private confirmOverwriteModalHandle = new ModalHandle();
}
